// src/scene/SceneCanvas.tsx
import React, { Suspense, useMemo } from 'react'
import { Canvas } from '@react-three/fiber'
import { PerspectiveCamera, OrbitControls, ContactShadows, Environment } from '@react-three/drei'
import { Model } from '../Model.tsx' // Assurez-vous que le chemin est correct

interface SceneCanvasProps {
  cameraRef: React.RefObject<any>
  controlsRef: React.RefObject<any>
  modelRef: React.RefObject<any>
  isMobile: boolean
  largeur: number
  nbCam: number
  textile: 'denim' | 'maille' | 'similicuir'
  texture: string
}

export const SceneCanvas: React.FC<SceneCanvasProps> = ({
  cameraRef,
  controlsRef,
  modelRef,
  isMobile,
  largeur,
  nbCam,
  textile,
  texture
}) => {
  
  // Calcule une cible par défaut stable pour éviter le reset à [0,0,0] lors du re-render
  const defaultTarget = useMemo(() => {
    return isMobile ? [1, 1, 0] : [0, 1.5, 0]
  }, [isMobile])

  return (
    <Canvas 
      shadows 
      dpr={[1, 2]} 
      gl={{ antialias: true, preserveDrawingBuffer: true }}
    >
      {/* ===== LÉGÈRE AMBIANCE LUMINEUSE ===== */}
      <ambientLight intensity={0.7} />
      <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} intensity={1} castShadow />
      <Environment preset="city" />

      {/* ===== CAMÉRA PILOTÉE PAR GSAP ===== */}
      <PerspectiveCamera
        ref={cameraRef}
        makeDefault
        fov={isMobile ? 50 : 35}
        // On ne met pas de position ici, SceneAnimation s'en occupe via gsap.set
      />

      {/* ===== CONTRÔLES PILOTÉS PAR GSAP ===== */}
      <OrbitControls
        ref={controlsRef}
        makeDefault
        enableRotate={false}
        enableZoom={false}
        enablePan={false}
        enableDamping={false} // Crucial : évite les conflits avec GSAP
        target={defaultTarget as [number, number, number]}
      />

      {/* ===== LE MODÈLE 3D ===== */}
      <Suspense fallback={null}>
        <group ref={modelRef}>
          <Model 
            largeur={largeur} 
            nbCam={nbCam} 
            textile={textile} 
            texture={texture} 
          />
        </group>
      </Suspense>

      {/* ===== EFFETS VISUELS ===== */}
      <ContactShadows 
        position={[0, -0.01, 0]} 
        opacity={0.4} 
        scale={20} 
        blur={2.4} 
        far={4.5} 
      />
    </Canvas>
  )
}